// Routine CRUD hooks. Mirrors the legacy RoutineService API shape but uses
// React Query for caching and invalidation.
import type {
  CreateRoutineInput,
  Routine,
  RoutineTemplateCatalog,
  UpdateRoutineInput,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

const ROUTINES_KEY = ['routines'] as const;

export function useRoutines() {
  return useQuery({
    queryKey: ROUTINES_KEY,
    queryFn: () => api<Routine[]>('/routines'),
  });
}

export function useRoutineTemplates() {
  return useQuery({
    queryKey: ['routine-templates'],
    queryFn: () => api<RoutineTemplateCatalog>('/routines/templates/catalog'),
  });
}

export function useCopyRoutineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api<{ routines: Routine[] }>(`/routines/templates/${templateId}/copy`, {
        method: 'POST',
        json: { folder: 'Templates' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROUTINES_KEY }),
  });
}

export function useRoutine(id: string | undefined) {
  return useQuery({
    queryKey: ['routines', id],
    queryFn: () => api<Routine>(`/routines/${id}`),
    enabled: !!id,
  });
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoutineInput) =>
      api<Routine>('/routines', { method: 'POST', json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTINES_KEY });
    },
  });
}

export function useUpdateRoutine(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRoutineInput) =>
      api<Routine>(`/routines/${id}`, { method: 'PATCH', json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTINES_KEY });
      void qc.invalidateQueries({ queryKey: ['routines', id] });
    },
  });
}

export function useDeleteRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/routines/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTINES_KEY });
    },
  });
}

// Records that the user just launched this routine — drives the "last used"
// label on the routine card.
export function useMarkRoutineUsed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Routine>(`/routines/${id}/used`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTINES_KEY });
    },
  });
}
